import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { hourlyFogForecastService } from '../../services/HourlyFogForecastService';
import { metarFogForecastService } from '../../services/MetarFogForecastService';
import { mapHourlyForecastToTimelineData, getAirportName } from '../../utils/HourlyForecastTimelineMapper';
import { mapForecastToHourlyTimelineData } from '../../utils/SharedForecastTimelineMapper';
import {
  HourlyForecastTimelineData,
  HourlyForecastTimelineSegment,
} from '../../models/HourlyForecastTimelineData';

export interface OperationalReplayPanel {
  issueTime: Date;
  windowEnd: Date;
  forecastSegments: HourlyForecastTimelineSegment[];
  actualsSegments: HourlyForecastTimelineSegment[];
}

export interface OperationalReplayData {
  locationId: number;
  locationName: string;
  referenceTime: Date;
  panels: OperationalReplayPanel[];
  segmentInterval: 30 | 60;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date;
}

const NUM_PANELS = 6;
const ISSUE_STEP_HOURS = 4;
const FORECAST_WINDOW_HOURS = 4;

/**
 * Builds an "operational replay" view: a stack of panels covering a 24h
 * lookback window. Each panel is a contiguous 4h window with 4h steps,
 * so the 6 panels tile T-24h..T with no overlap. The latest issue is at
 * T-4h so its forecast window ends at T — ensuring all panels have fully
 * elapsed actuals.
 */
export function useOperationalReplayData(
  locationId: number | null,
  isMetarLocation: boolean,
  customNow?: Date | null
): OperationalReplayData {
  const [panels, setPanels] = useState<OperationalReplayPanel[]>([]);
  const [segmentInterval, setSegmentInterval] = useState<30 | 60>(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [locationName, setLocationName] = useState('');
  const isMounted = useRef(true);

  const referenceTime = useMemo(() => customNow || new Date(), [customNow]);

  const fetchData = useCallback(async () => {
    if (!locationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const name = getAirportName(locationId);
    setLocationName(name);
    const ref = customNow || new Date();

    // Round centre point down to the nearest hour for clean issue boundaries
    const centerPoint = new Date(ref);
    centerPoint.setUTCMinutes(0, 0, 0);

    // Issue times: oldest first. The latest issue is at T-4h so its forecast
    // window (R..R+4h) ends at T — ensuring all panels have fully elapsed
    // actuals. With 4h steps and 6 panels: [T-24h, T-20h, ..., T-4h].
    const latestIssueOffset = FORECAST_WINDOW_HOURS;
    const issueTimes: Date[] = [];
    for (let i = NUM_PANELS - 1; i >= 0; i--) {
      issueTimes.push(new Date(centerPoint.getTime() - (latestIssueOffset + i * ISSUE_STEP_HOURS) * 60 * 60 * 1000));
    }

    // Each call at requested_time=R returns actuals R-4h..R. The issue
    // calls cover T-28h..T-4h in actuals. We need one extra call at T
    // (centerPoint) to get actuals for the last panel (T-4h..T). Only
    // include it if T <= real now. Also add an unrounded ref call so
    // actuals reach exactly up to the cursor.
    const realNow = Date.now();
    const actualsAtNow = centerPoint.getTime() <= realNow ? [centerPoint] : [];
    const extraActualsCall = ref.getTime() !== centerPoint.getTime()
      && ref.getTime() <= realNow ? [ref] : [];
    const callTimes: Date[] = [...issueTimes, ...actualsAtNow, ...extraActualsCall];

    try {
      const results = await Promise.allSettled(
        callTimes.map(reqTime => {
          if (isMetarLocation) {
            return metarFogForecastService.getBatchMetarFogForecast([locationId], reqTime)
              .then(result => {
                const forecast = result.forecasts[0];
                if (!forecast) throw new Error('No METAR forecast returned');
                return mapForecastToHourlyTimelineData(forecast, locationId, name);
              });
          } else {
            return hourlyFogForecastService.getBatchHourlyFogForecast([locationId], reqTime)
              .then(result => {
                const forecast = result.forecasts[0];
                if (!forecast) throw new Error('No hourly forecast returned');
                return mapHourlyForecastToTimelineData(forecast, locationId, name);
              });
          }
        })
      );

      if (!isMounted.current) return;

      // Need at least one successful call
      const anySuccess = results.some(r => r.status === 'fulfilled');
      if (!anySuccess) {
        setError('Failed to load operational replay data');
        setLoading(false);
        return;
      }

      // Capture segment_interval from first successful call
      const firstSuccess = results.find(r => r.status === 'fulfilled') as
        PromiseFulfilledResult<HourlyForecastTimelineData> | undefined;
      const interval = (firstSuccess?.value.segment_interval || 60) as 30 | 60;
      setSegmentInterval(interval);

      // Combine all actuals across all calls, dedupe by timestamp
      const actualsByTime = new Map<number, HourlyForecastTimelineSegment>();
      for (const r of results) {
        if (r.status === 'fulfilled') {
          for (const seg of r.value.segments) {
            if (seg.raw_actuals !== null) {
              actualsByTime.set(seg.time.getTime(), seg);
            }
          }
        }
      }

      const newPanels: OperationalReplayPanel[] = issueTimes.map((issueTime, idx) => {
        const windowEnd = new Date(issueTime.getTime() + FORECAST_WINDOW_HOURS * 60 * 60 * 1000);
        const r = results[idx];

        let forecastSegments: HourlyForecastTimelineSegment[] = [];
        if (r.status === 'fulfilled') {
          forecastSegments = r.value.segments
            .filter(s => s.raw_predictions !== null
              && s.time.getTime() >= issueTime.getTime()
              && s.time.getTime() < windowEnd.getTime());
        }

        const actualsSegments: HourlyForecastTimelineSegment[] = [];
        for (const [t, seg] of actualsByTime.entries()) {
          if (t >= issueTime.getTime() && t < windowEnd.getTime()) {
            actualsSegments.push(seg);
          }
        }
        actualsSegments.sort((a, b) => a.time.getTime() - b.time.getTime());

        return { issueTime, windowEnd, forecastSegments, actualsSegments };
      });

      setPanels(newPanels);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching operational replay data:', err);
      if (!isMounted.current) return;
      setError('Failed to load operational replay data');
      setLoading(false);
    }
  }, [locationId, isMetarLocation, customNow]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    locationId: locationId || 0,
    locationName,
    referenceTime,
    panels,
    segmentInterval,
    loading,
    error,
    refresh: fetchData,
    lastUpdated,
  };
}
